import { gql } from '@apollo/client';

export const ASSESSMENTS_LIST = gql`
  query assessmentsList($workspaceId: UxId) {
    assessments(workspaceId: $workspaceId) {
      id
      name
      description
      AssessmentLiveRisk {
        expectedLoss
      }
      startedDate
      reviewDate
      scenarios {
        id
        isIncomplete
      }
    }
  }
`;

export const ASSESSMENTS = gql`
  query assessments($workspaceId: UxId) {
    assessments(workspaceId: $workspaceId) {
      id
      name
      description
      AssessmentLiveRisk {
        expectedLoss
      }
      startedDate
      reviewDate
      scenarios {
        id
        isIncomplete
        name
        ownerName
        liveRisk {
          expectedLoss
        }
        source
        event
        consequence
        riskIdentifiedDate
        editedAt
        status
        meta {
          createdAt
          updatedAt
        }
        assessment {
          id
        }
      }
    }
  }
`;

export const ASSESSMENTS_SCENARIO_NAMES = gql`
  query assessmentsScenarioNames($workspaceId: UxId) {
    assessments(workspaceId: $workspaceId) {
      id
      name
      scenarios {
        id
        name
      }
    }
  }
`;

export const INCIDENTS = gql`
  query incidents($workspaceId: UxId) {
    incidents(workspaceId: $workspaceId) {
      id
      name
      ref
      source
      incidentType
      dateOfIncident
      description
      financialImpactLower
      financialImpactUpper
      impact
      severity
      owner {
        id
        text
        email
        inviteId
        memberId
        profilePhotoPath
      }
      scenarios {
        id
      }
    }
  }
`;

export const INCIDENTS_CHECK = gql`
  query incidentsCheck($workspaceId: UxId) {
    incidents(workspaceId: $workspaceId) {
      id
      scenarios {
        id
      }
    }
  }
`;

export const INCIDENT_LINKED_SCENARIOS = gql`
  query incidentLinkedScenarios($id: UxId!, $versionId: UxId) {
    incident(id: $id, versionId: $versionId) {
      id
      scenarios {
        id
        name
        assessment {
          id
          name
        }
        source
        event
        consequence
        liveRisk {
          expectedLoss
        }
        ownerName
        riskIdentifiedDate
        isIncomplete
        editedAt
        status
        riskManagementPlans {
          id
        }
        meta {
          createdAt
          updatedAt
        }
      }
    }
  }
`;

export const INCIDENT = gql`
  query incident($id: UxId!, $versionId: UxId) {
    incident(id: $id, versionId: $versionId) {
      id
      name
      ref
      source
      incidentType
      dateOfIncident
      description
      financialImpactLower
      financialImpactUpper
      impact
      severity
      owner {
        id
        text
        email
        inviteId
        memberId
        profilePhotoPath
      }
    }
  }
`;

export const SCENARIO_TEMPLATES = gql`
  query scenarioTemplates {
    scenarioTemplates {
      id
      name
      author
      groupName
      description
      source
      event
      consequence
      riskOwner
      riskNarrative
      expectedLoss
      frequencyTimes
      frequencyYears
      frequencyNote
      managementStrategy
      lowerFormula
      upperFormula
      note
    }
  }
`;

export const SCENARIO_GROUPS = gql`
  query scenarioGroups($workspaceId: UxId) {
    scenarioGroups(workspaceId: $workspaceId) {
      id
      name
      description
      author
      authorLogoPath
      organisation {
        id
        name
        logoUrl
      }
      category
      featureKey
      isSystem
      versionNumber
      isCurrentVersion
      versionCreatedBy
      meta {
        createdAt
        updatedAt
      }
      versions {
        id
        name
        versionNumber
        isCurrentVersion
        versionCreatedBy
        scenarios {
          id
        }
        meta {
          createdAt
          updatedAt
        }
      }
      scenarios {
        id
        meta {
          createdAt
          updatedAt
        }
      }
    }
  }
`;

export const SCENARIO_GROUPS_WITH_SCENARIOS = gql`
  query scenarioGroupsWithScenarios($workspaceId: UxId) {
    scenarioGroups(workspaceId: $workspaceId) {
      id
      name
      description
      author
      authorLogoPath
      organisation {
        id
        name
        logoUrl
      }
      category
      featureKey
      isSystem
      versionNumber
      isCurrentVersion
      versionCreatedBy
      meta {
        createdAt
        updatedAt
      }
      versions {
        id
        name
        versionNumber
        isCurrentVersion
        versionCreatedBy
        scenarios {
          id
        }
        meta {
          createdAt
          updatedAt
        }
      }
      scenarios {
        id
        name
        description
        ownerName
        source
        event
        consequence
        riskNarrative
        frequencyNote
        managementStrategy
        managementNote
        consequenceNote
        lowerFormula
        upperFormula
        status
        riskIdentifiedDate
        reviewDate
        assessment {
          id
        }
        baseRisk {
          frequencyTimes
          frequencyYears
          expectedLoss
          lower
          upper
        }
        projectedRisk {
          frequencyTimes
          frequencyYears
          expectedLoss
          lower
          upper
        }
        liveRisk {
          frequencyTimes
          frequencyYears
          expectedLoss
          lower
          upper
        }
        meta {
          createdAt
          updatedAt
        }
      }
    }
  }
`;

export const CONTROLS = gql`
  query controls($workspaceId: UxId, $selection: ControlSelectionInput) {
    controls(workspaceId: $workspaceId, selection: $selection) {
      id
      name
      description
      ownerEmail
      ownerName
      annualCost
      annualCostFormula
      implementationCost
      implementationCostFormula
      costNote
      vendor
      strategy
      status
      origin
      linkUrl
      linkText
      policyUrl
      policyUrlText
      expectedActiveDate
      actualActiveDate
      reviewDate
      existingMaturity
      targetMaturity
      # CRPD-7274 — the Actions column's count, TEMPORARY. There is no actionsCount field on the
      # schema, so we ask for the ids and take the length. This is a per-parent field resolver
      # (EventResolver@actionsForControl opens a fresh Event query per control), so it costs one
      # query PER ROW on a document this page refetches after every maturity edit. first is
      # raised off its default 50 so the number is not silently capped. Replace the whole block
      # with a single actionsCount the day the API ships one — see be.md.
      actions(first: 200) {
        id
      }
      frameworkControls {
        id
        name
        shortname
        control_identifier
        # CRPD-7274 — the control's position in its framework's hierarchy. Read by the Control
        # Maturity table and by the RMP's Control Tier column. Deliberately on THIS document
        # rather than a second controls operation: Apollo keys a root field by name + argument
        # values, so a second operation with matching args shares one store entry and the
        # narrower write silently truncates the wider one.
        hierarchyPath
        controlFramework {
          id
          name
          shortname
          framework_identifier
          # frameworkMaturity scopes to is_current_version = 1; a control linked to a
          # superseded version must not be rendered under a tier whose average excludes it.
          isCurrentVersion
        }
      }
      riskManagementPlansCount
      meta {
        createdAt
        updatedAt
      }
    }
  }
`;

/**
 * CRPD-7209/7222 — variables referenced by the given templates'/controls' cost
 * formulas that are unpopulated in the workspace. Drives the add-to-RMP nudge.
 */
export const CONTROL_COST_MISSING_VARIABLES = gql`
  query controlCostMissingVariables($workspaceId: UxId!, $frameworkControlIds: [UxId!], $controlIds: [UxId!]) {
    controlCostMissingVariables(
      workspaceId: $workspaceId
      frameworkControlIds: $frameworkControlIds
      controlIds: $controlIds
    ) {
      id
      name
    }
  }
`;

export const CONTROLS_RMP_CHECK = gql`
  query controlsRmpCheck($workspaceId: UxId) {
    controls(workspaceId: $workspaceId) {
      id
      frameworkControls {
        id
        control_identifier
      }
    }
  }
`;

export const CONTROL_RMP = gql`
  query controlRmp($id: UxId!, $versionId: UxId) {
    control(id: $id, versionId: $versionId) {
      id
      strategy
      status
      riskManagementPlans {
        id
        startingFrequency
        startingLower
        startingUpper
        modifiers {
          id
          type
          consequenceUpper
          consequenceLower
          frequencyReduction
          isPercentage
          totalPolicyCover
          totalPolicyCoverFormula
          excess
          excessFormula
          note
        }
        recommendedModifiers {
          type
          data {
            upper
            lower
            value
            isPercentage
          }
        }
        scenario {
          id
          name
          source
          event
          consequence
          ownerName
          isIncomplete
          assessment {
            id
            name
          }
          baseRisk {
            upper
            lower
            frequencyTimes
            frequencyYears
          }
          riskIdentifiedDate
          editedAt
          liveRisk {
            expectedLoss
          }
          status

          meta {
            createdAt
            updatedAt
          }
        }
      }
    }
  }
`;

// Minimal query for Control page expand content (Modifier only)
export const CONTROL_MODIFIER = gql`
  query controlModifier($id: UxId!) {
    control(id: $id) {
      id
      strategy
      status
      riskManagementPlans {
        id
        modifiers {
          id
          type
          consequenceUpper
          consequenceLower
          frequencyReduction
          isPercentage
          totalPolicyCover
          totalPolicyCoverFormula
          excess
          excessFormula
          note
        }
        recommendedModifiers {
          type
          data {
            upper
            lower
            value
            isPercentage
          }
        }
        scenario {
          id
          assessment {
            id
            name
          }
          baseRisk {
            upper
            lower
            frequencyTimes
            frequencyYears
          }
        }
      }
    }
  }
`;

export const CONTROLS_RMP = gql`
  query controlsRmp($workspaceId: UxId!) {
    controls(workspaceId: $workspaceId) {
      id
      frameworkControls {
        id
        control_identifier
        controlFramework {
          id
        }
      }
      riskManagementPlans {
        id
        scenario {
          id
        }
      }
    }
  }
`;

export const SCENARIO_RMP = gql`
  query scenarioRmp($id: UxId!, $versionId: UxId) {
    scenario(id: $id, versionId: $versionId) {
      id
      assessment {
        id
        name
      }
      baseRisk {
        upper
        lower
        frequencyTimes
        frequencyYears
      }
      riskManagementPlans {
        id
        startingFrequency
        startingLower
        startingUpper
        control {
          id
          name
          strategy
          ownerName
          annualCost
          status
          existingMaturity
          targetMaturity
          linkUrl
          linkText
          policyUrl
          policyUrlText
          frameworkControls {
            id
            name
            shortname
            control_identifier
            strategy
            controlFramework {
              id
              name
              shortname
            }
          }
          meta {
            createdAt
            updatedAt
          }
        }
        modifiers {
          id
          type
          consequenceUpper
          consequenceLower
          frequencyReduction
          isPercentage
          totalPolicyCover
          totalPolicyCoverFormula
          excess
          excessFormula
          note
        }
        recommendedModifiers {
          type
          data {
            upper
            lower
            value
            isPercentage
          }
        }
      }
    }
  }
`;

// Minimal query for Scenario page expand content (Modifier only)
export const SCENARIO_MODIFIER = gql`
  query scenarioModifier($id: UxId!, $versionId: UxId) {
    scenario(id: $id, versionId: $versionId) {
      id
      baseRisk {
        upper
        lower
        frequencyTimes
        frequencyYears
      }
      riskManagementPlans {
        id
        control {
          id
          strategy
          status
        }
        modifiers {
          id
          type
          consequenceUpper
          consequenceLower
          frequencyReduction
          isPercentage
          totalPolicyCover
          totalPolicyCoverFormula
          excess
          excessFormula
          note
        }
        recommendedModifiers {
          type
          data {
            upper
            lower
            value
            isPercentage
          }
        }
      }
    }
  }
`;

export const CONTROL_FRAMEWORKS_LIST = gql`
  query controlFrameworksList {
    controlFrameworks {
      id
      name
      shortname
      controlCount
      author
      organisation {
        id
        name
        logoUrl
      }
      featureKey
      versionNumber
      isCurrentVersion
      versionCreatedBy
      meta {
        createdAt
        updatedAt
      }
      versions {
        id
        name
        shortname
        controlCount
        versionNumber
        isCurrentVersion
        versionCreatedBy
        frameworkControls {
          id
        }
        meta {
          createdAt
          updatedAt
        }
      }
      frameworkControls {
        id
        shortname
        control_identifier
      }
    }
  }
`;

export const CONTROL_FRAMEWORK_DETAILS = gql`
  query controlFrameworkDetails($id: UxId) {
    controlFrameworks(id: $id) {
      id
      name
      shortname
      author
      description
      frameworkControls {
        id
        name
        shortname
        control_identifier
        strategy
        annualCostFormula
        implementationCostFormula
        costNote
        controlFramework {
          id
          shortname
          author
          description
        }
      }
    }
  }
`;

export const FRAMEWORK_CONTROL = gql`
  query frameworkControl($id: UxId!) {
    frameworkControl(id: $id) {
      id
      name
      shortname
      description
      control_identifier
      strategy
      annualCostFormula
      implementationCostFormula
      costNote
      controlFramework {
        id
        name
        shortname
        author
        framework_identifier
        externalId
      }
    }
  }
`;

export const INSIGHTS = gql`
  query insights($id: UxId!) {
    assessment(id: $id) {
      id
      name
      AssessmentLiveRisk {
        expectedLoss
      }
      scenarios {
        id
        name
        liveRisk {
          expectedLoss
        }
        incidents {
          id
          financialImpactLower
          financialImpactUpper
          dateOfIncident
          incidentType
        }
        reviewDate
        riskManagementPlans {
          id
          control {
            id
            name
            status
            existingMaturity
            targetMaturity
          }
        }
      }
    }
  }
`;

export const ASSESSMENT = gql`
  query assessment($id: UxId!, $versionId: UxId) {
    assessment(id: $id, versionId: $versionId) {
      id
      name
      description
      executiveSummary
      riskTolerance
      AssessmentBaseRisk {
        expectedLoss
        chart {
          x
          y
        }
      }
      AssessmentProjectedRisk {
        expectedLoss
        chart {
          x
          y
        }
      }
      AssessmentLiveRisk {
        expectedLoss
        chart {
          x
          y
        }
      }
      startedDate
      reviewDate
      variables {
        id
        name
        value
        isInUse
        currency
        isGlobal
        alias
        workspaceVariable
      }
      scenarios {
        id
        name
        isIncomplete
        baseRisk {
          upper
          lower
        }
        liveRisk {
          upper
          lower
        }
        projectedRisk {
          upper
          lower
        }
        riskManagementPlans {
          id
          control {
            id
            status
          }
          modifiers {
            id
            type
          }
        }
      }
      versions {
        id
        name
        editable
        legacyVersion
        meta {
          createdBy
          createdAt
        }
      }
    }
  }
`;

export const ASSESSMENT_VARIABLES = gql`
  query assessmentVariables($id: UxId!) {
    assessment(id: $id) {
      id
      variables {
        id
        name
        value
        isInUse
        currency
        isGlobal
        alias
        workspaceVariable
      }
    }
  }
`;

export const ASSESSMENT_RISK_REGISTER = gql`
  query assessmentRiskRegister($id: UxId!, $versionId: UxId) {
    assessment(id: $id, versionId: $versionId) {
      id
      name
      scenarios {
        id
        name
        source
        event
        consequence
        ownerName
        riskOwner {
          text
        }
        riskIdentifiedDate
        reviewDate
        editedAt
        isIncomplete
        riskNarrative
        frequencyNote
        consequenceNote
        lowerFormula
        upperFormula
        baseRisk {
          expectedLoss
          frequencyTimes
          frequencyYears
          lower
          upper
        }
        projectedRisk {
          expectedLoss
          frequencyTimes
          frequencyYears
          lower
          upper
        }
        liveRisk {
          expectedLoss
          frequencyTimes
          frequencyYears
          lower
          upper
        }
        managementStrategy
        managementNote
        ownerName
        riskManagementPlans {
          id
          control {
            id
            status
            frameworkControls {
              id
              control_identifier
            }
          }
          modifiers {
            id
            type
          }
        }
        status
        meta {
          createdAt
          updatedAt
        }
      }
    }
  }
`;

export const ASSESSMENT_SCENARIOS_WIZARD = gql`
  query assessmentScenariosWizard($id: UxId!) {
    assessment(id: $id) {
      id
      name
      scenarios {
        id
        name
        source
        event
        consequence
        ownerName
        riskIdentifiedDate
        editedAt
        isIncomplete
        liveRisk {
          expectedLoss
        }
        riskManagementPlans {
          id
          control {
            id
            frameworkControls {
              id
              control_identifier
            }
          }
        }
        status
        meta {
          createdAt
          updatedAt
        }
      }
    }
  }
`;

export const CONTROL = gql`
  query control($id: UxId!, $versionId: UxId) {
    control(id: $id, versionId: $versionId) {
      id
      name
      shortname
      description
      owner {
        id
        text
        email
        inviteId
        memberId
        profilePhotoPath
      }
      annualCost
      annualCostFormula
      implementationCost
      implementationCostFormula
      costNote
      vendor
      strategy
      status
      origin
      linkUrl
      linkText
      policyUrl
      policyUrlText
      expectedActiveDate
      actualActiveDate
      reviewDate
      riskManagementPlans {
        id
      }
      frameworkControls {
        id
        control_identifier
        controlFramework {
          id
          shortname
        }
        compatibleControls {
          id
          name
          shortname
          control_identifier
          description
          strategy
          controlFramework {
            id
            name
            shortname
            description
            author
            framework_identifier
            externalId
          }
        }
      }
      existingMaturity
      targetMaturity
      meta {
        createdAt
        updatedAt
      }
    }
  }
`;

export const CONTROL_LINKED_SCENARIOS = gql`
  query controlLinkedScenarios($id: UxId!, $versionId: UxId) {
    control(id: $id, versionId: $versionId) {
      id
      strategy
      riskManagementPlans {
        id
        control {
          id
          strategy
        }
        scenario {
          id
          name
          source
          event
          consequence
          ownerName
          riskIdentifiedDate
          editedAt
          isIncomplete
          liveRisk {
            expectedLoss
          }
          assessment {
            id
            name
          }
          riskManagementPlans {
            id
          }
          status
          meta {
            createdAt
            updatedAt
          }
        }
        modifiers {
          id
          type
        }
      }
    }
  }
`;

export const SCENARIO = gql`
  query scenario($id: UxId!, $versionId: UxId) {
    scenario(id: $id, versionId: $versionId) {
      id
      name
      managementNote
      managementStrategy
      source
      event
      consequence
      riskIdentifiedDate
      riskNarrative
      frequencyNote
      consequenceNote
      isIncomplete
      status
      reviewDate
      groupName
      lowerFormula
      upperFormula
      assessment {
        id
        name
      }
      riskOwner {
        text
        email
        id
        profilePhotoPath
      }
      baseRisk {
        expectedLoss
        frequencyTimes
        frequencyYears
        upper
        lower
        chart {
          x
          y
        }
      }
      projectedRisk {
        expectedLoss
        frequencyTimes
        frequencyYears
        lower
        upper
        chart {
          x
          y
        }
      }
      liveRisk {
        expectedLoss
        frequencyTimes
        frequencyYears
        lower
        upper
        lowerThanMinimum
        chart {
          x
          y
        }
      }
      riskManagementPlans {
        id
        control {
          id
          status
        }
        modifiers {
          id
          type
        }
      }
      editedAt
      meta {
        updatedAt
      }
    }
  }
`;

export const SCENARIO_INCIDENTS = gql`
  query scenarioIncidents($id: UxId!, $versionId: UxId) {
    scenario(id: $id, versionId: $versionId) {
      id
      name
      incidents {
        id
        name
        ref
        source
        incidentType
        dateOfIncident
        description
        financialImpactLower
        financialImpactUpper
        impact
        severity
        owner {
          id
          text
          email
          inviteId
          memberId
          profilePhotoPath
        }
        scenarios {
          id
        }
      }
    }
  }
`;

export const DASHBOARD = gql`
  query dashboard($assessmentId: UxId!) {
    assessment(id: $assessmentId) {
      id
      name
      description
      startedDate
      editedAt
      scenarios {
        id
        isIncomplete
        riskManagementPlans {
          id
          control {
            id
            status
          }
          modifiers {
            id
          }
        }
        incidents {
          id
        }
      }
      meta {
        updatedAt
      }
    }
    dashboard(assessmentId: $assessmentId) {
      expectedLoss {
        unmodifiedRisk
        liveRisk
        projectedRisk
      }
      securityProgram {
        riskReduction
        cost
        savings
      }
      futureSecurityProgram {
        furtherReduction
        additionalCost
        totalSavings
      }
      returnOnInvestment {
        currentProgramme
        futureProgramme
      }
      riskyScenarios {
        scenarios {
          id
          name
          liveRisk {
            expectedLoss
          }
          assessment {
            id
          }
        }
      }
      controlImplementation {
        notStartedCount
        notStartedCost
        inProgressCount
        inProgressCost
        liveCount
        liveCost
        otherCount
        otherCost
        controlsWithoutCost
      }
      overdueReviews {
        assessments
        scenarios
        controls
      }
      averageIncidentFrequency {
        meantimeBetweenIncidents
        meantimeBetweenNearMisses
      }
      riskAvoidance {
        nearMisses
        days
        estimatedLossLower
        estimatedLossUpper
      }
      missingData {
        scenariosWithoutCalculationData
        scenariosWithoutLinkedControls
        scenariosWithoutModifiers
      }
    }
  }
`;

export const VERSIONS = gql`
  query versions($assessmentId: UxId!) {
    assessment(id: $assessmentId) {
      id
      versions {
        id
        name
        editable
        legacyVersion
        meta {
          createdBy
          createdAt
        }
      }
    }
  }
`;

// export const TIMELINE = gql`
//   query timeline($workspaceId: UxId!, $filters: TimelineFilterInput) {
//     timeline(workspaceId: $workspaceId, filters: $filters) {
//       total
//       today
//       filters {
//         events
//         owners
//         objects
//       }
//       data {
//         index
//         event_date
//         events {
//           type
//           data
//         }
//       }
//     }
//   }
// `;

const EVENT_FIELDS = gql`
  fragment EventFields on Event {
    id
    objectType
    objectId
    eventType
    notes
    assignedTo {
      id
      text
      email
      inviteId
      memberId
      profilePhotoPath
    }
    dueDate
    completionStatus
    completedBy {
      id
      text
      email
      inviteId
      memberId
      profilePhotoPath
    }
    completedAt
    isOverdue
    meta {
      createdAt
      updatedAt
      createdBy
    }
  }
`;

export const EVENTS = gql`
  ${EVENT_FIELDS}
  query events(
    $workspaceId: UxId!
    $objectType: ObjectKind
    $objectId: UxId
    $eventType: EventKind
    $completionStatus: EventCompletionStatus
    $onlyOpen: Boolean = false
  ) {
    events(
      workspaceId: $workspaceId
      objectType: $objectType
      objectId: $objectId
      eventType: $eventType
      completionStatus: $completionStatus
      onlyOpen: $onlyOpen
    ) {
      ...EventFields
    }
  }
`;

export const EVENT = gql`
  ${EVENT_FIELDS}
  query event($id: UxId!) {
    event(id: $id) {
      ...EventFields
    }
  }
`;

const COMPARISON_ASSESSMENT_FRAGMENT = gql`
  fragment ComparisonAssessmentData on Assessment {
    id
    name
    riskTolerance
    reviewDate
    AssessmentBaseRisk {
      expectedLoss
      chart {
        x
        y
      }
    }
    AssessmentProjectedRisk {
      expectedLoss
      chart {
        x
        y
      }
    }
    AssessmentLiveRisk {
      expectedLoss
      chart {
        x
        y
      }
    }
    scenarios {
      id
      name
      source
      event
      consequence
      ownerName
      riskOwner {
        text
      }
      referenceId
      isIncomplete
      lowerFormula
      upperFormula
      baseRisk {
        expectedLoss
        frequencyTimes
        frequencyYears
        lower
        upper
      }
      projectedRisk {
        expectedLoss
        frequencyTimes
        frequencyYears
        lower
        upper
      }
      liveRisk {
        expectedLoss
        frequencyTimes
        frequencyYears
        lower
        upper
        lowerThanMinimum
      }
      riskManagementPlans {
        id
        control {
          id
          status
          frameworkControls {
            id
            control_identifier
          }
        }
        modifiers {
          id
          type
        }
      }
      managementStrategy
      managementNote
      riskIdentifiedDate
      reviewDate
      editedAt
      riskNarrative
      frequencyNote
      consequenceNote
      status
      meta {
        createdAt
        updatedAt
      }
    }
    versions {
      id
      name
      editable
      legacyVersion
      meta {
        createdBy
        createdAt
      }
    }
  }
`;

export const COMPARISON_ASSESSMENT = gql`
  ${COMPARISON_ASSESSMENT_FRAGMENT}
  query comparisonAssessment($assessmentAId: UxId!, $assessmentBId: UxId!, $versionAId: UxId, $versionBId: UxId) {
    assessmentA: assessment(id: $assessmentAId, versionId: $versionAId) {
      ...ComparisonAssessmentData
    }
    assessmentB: assessment(id: $assessmentBId, versionId: $versionBId) {
      ...ComparisonAssessmentData
    }
  }
`;

const COMPARISON_SCENARIO_FRAGMENT = gql`
  fragment ComparisonScenarioData on Scenario {
    id
    name
    source
    event
    consequence
    riskOwner {
      text
      email
      id
      profilePhotoPath
    }
    riskNarrative
    frequencyNote
    consequenceNote
    managementStrategy
    managementNote
    status
    reviewDate
    riskIdentifiedDate
    editedAt
    baseRisk {
      expectedLoss
      frequencyTimes
      frequencyYears
      lower
      upper
      chart {
        x
        y
      }
    }
    projectedRisk {
      expectedLoss
      frequencyTimes
      frequencyYears
      lower
      upper
      chart {
        x
        y
      }
    }
    liveRisk {
      expectedLoss
      frequencyTimes
      frequencyYears
      lower
      upper
      lowerThanMinimum
      chart {
        x
        y
      }
    }
    assessment {
      id
      name
      versions {
        id
        name
        editable
        legacyVersion
        meta {
          createdBy
          createdAt
        }
      }
    }
  }
`;

export const COMPARE_SCENARIOS = gql`
  ${COMPARISON_SCENARIO_FRAGMENT}
  query compareScenarios($scenarioAId: UxId!, $scenarioBId: UxId!, $versionAId: UxId, $versionBId: UxId) {
    scenarioA: scenario(id: $scenarioAId, versionId: $versionAId) {
      ...ComparisonScenarioData
    }
    scenarioB: scenario(id: $scenarioBId, versionId: $versionBId) {
      ...ComparisonScenarioData
    }
  }
`;

export const SCENARIO_CONTROLS = gql`
  query scenarioControls($id: UxId!, $versionId: UxId) {
    scenario(id: $id, versionId: $versionId) {
      id
      name
      baseRisk {
        upper
        lower
        frequencyTimes
        frequencyYears
      }
      riskManagementPlans {
        id
        startingFrequency
        startingLower
        startingUpper
        order
        control {
          id
          name
          shortname
          description
          strategy
          status
          origin
          annualCost
          vendor
          linkUrl
          linkText
          policyUrl
          policyUrlText
          expectedActiveDate
          actualActiveDate
          reviewDate
          existingMaturity
          targetMaturity
          owner {
            id
            text
            email
            memberId
            inviteId
            profilePhotoPath
          }
          frameworkControls {
            id
            name
            shortname
            control_identifier
            strategy
            controlFramework {
              id
              name
              shortname
            }
          }
          meta {
            createdAt
            updatedAt
          }
        }
        modifiers {
          id
          type
        }
      }
    }
  }
`;

export const FILTERS_FOR = gql`
  query filtersFor($workspaceId: UxId!, $object: FilterObjectKey!, $includeCounts: Boolean = false) {
    filtersFor(workspaceId: $workspaceId, object: $object, includeCounts: $includeCounts) {
      controls {
        strategy {
          unifiedKey
          label
          enabled
        }
        status {
          unifiedKey
          label
          enabled
        }
        owners {
          unifiedKey
          label
          enabled
          email
        }
        frameworks {
          unifiedKey
          label
          enabled
        }
        assessments {
          unifiedKey
          label
          enabled
        }
        existingMaturity {
          unifiedKey
          label
          enabled
        }
        targetMaturity {
          unifiedKey
          label
          enabled
        }
      }
    }
  }
`;

export const COMPLIANCE_FRAMEWORK_GAUGE = gql`
  query complianceFrameworkGauge($id: UxId!, $workspaceId: UxId!) {
    complianceFrameworks(id: $id, workspaceId: $workspaceId) {
      controlsCount
      controlsImplementedCount
      controlsPlannedCount
    }
  }
`;

export const COMPLIANCE_FRAMEWORK = gql`
  query complianceFramework($id: UxId!, $workspaceId: UxId!) {
    complianceFrameworks(id: $id, workspaceId: $workspaceId) {
      controlsCount
      controlsImplementedCount
      controlsPlannedCount
      controlFramework {
        id
        shortname
        name
        description
        author
        framework_identifier
        featureKey
        version
        versionNumber
        isCurrentVersion
        versionCreatedBy
        publishDate
        meta {
          createdAt
          updatedAt
        }
        versions {
          id
          name
          shortname
          controlCount
          versionNumber
          isCurrentVersion
          versionCreatedBy
          frameworkControls {
            id
          }
          meta {
            createdAt
            updatedAt
          }
        }
      }
      complianceControls {
        complianceStatus
        frameworkControl {
          id
          control_identifier
          name
          shortname
          strategy
          description
          # CRPD-7267 — the control's tier path, for the workspace framework page's Hierarchy
          # column and its Hierarchy view.
          hierarchyPath
          annualCostFormula
          implementationCostFormula
          costNote
          meta {
            createdAt
            updatedAt
          }
          controlFramework {
            shortname
            author
          }
        }
        compliantRmpControls {
          id
          name
          strategy
          status
          owner {
            id
            text
          }
          frameworkControls {
            controlFramework {
              id
              shortname
            }
          }
        }
      }
    }
  }
`;

export const RISKY_SCENARIOS_PREVIEW = gql`
  query riskyScenariosPreview($assessmentId: UxId!) {
    dashboard(assessmentId: $assessmentId) {
      riskyScenarios {
        scenarios {
          id
          name
          source
          event
          consequence
          riskNarrative
          reviewDate
          riskOwner {
            text
            email
          }
          liveRisk {
            expectedLoss
            lower
            upper
            frequencyTimes
            frequencyYears
          }
        }
      }
    }
  }
`;

export const SCENARIO_REGISTER_PREVIEW = gql`
  query scenarioRegisterPreview($assessmentId: UxId!) {
    assessment(id: $assessmentId) {
      id
      name
      scenarios {
        id
        name
        source
        event
        consequence
        ownerName
        reviewDate
        riskOwner {
          text
          email
        }
        liveRisk {
          expectedLoss
          lower
          upper
          frequencyTimes
          frequencyYears
        }
        riskManagementPlans {
          id
          control {
            id
            status
          }
        }
      }
    }
  }
`;

/**
 * CRPD-7274 — the spider chart and the Maturity Score Table.
 *
 * Both depths live in ONE operation, aliased, for two reasons. `frameworkMaturity` groups at
 * exactly one depth per call, so the nested table needs depth 1 (the spider's legs) and depth 2
 * (its sub-tiers) simultaneously; and a single operation name means one `refetchQuery` refreshes
 * both, which is what stops an inline edit updating the tier rows while the sub-tiers go stale.
 *
 * Consumers must key tiers by (depth, path), never by path alone: a framework control placed
 * directly at `ID` yields a `{path:'ID', depth:1}` row in BOTH responses — covering everything
 * under ID at depth 1, but only the directly-placed controls at depth 2.
 */
export const FRAMEWORK_MATURITY = gql`
  query frameworkMaturity($frameworkIdentifier: String!, $workspaceId: UxId) {
    tier1: frameworkMaturity(frameworkIdentifier: $frameworkIdentifier, depth: 1, workspaceId: $workspaceId) {
      frameworkIdentifier
      depth
      # Selected but not rendered. It counts framework controls the framework's own hierarchy
      # never placed — NOT the spec's "Controls Not Included in Visualization", which is the
      # source-framework banner's RMP-side count. The line that displayed it was removed as
      # unrequested (CRPD-7274 QA); kept selected because CRPD-7331 may want it back.
      unplacedFrameworkControlCount
      overall {
        currentMaturity
        targetMaturity
        scoredCount
        totalCount
        scoredRmpControlCount
      }
      tiers {
        path
        depth
        label
        description
        commentary
        currentMaturity
        targetMaturity
        frameworkControlCount
        currentScoredCount
        targetScoredCount
        scoredRmpControlCount
        linkedRmpControlCount
      }
    }
    tier2: frameworkMaturity(frameworkIdentifier: $frameworkIdentifier, depth: 2, workspaceId: $workspaceId) {
      depth
      tiers {
        path
        depth
        label
        description
        commentary
        currentMaturity
        targetMaturity
        frameworkControlCount
        currentScoredCount
        targetScoredCount
        scoredRmpControlCount
        linkedRmpControlCount
      }
    }
  }
`;

/** CRPD-7274 — this workspace's tier commentary, read when the tier panel opens. */
export const FRAMEWORK_HIERARCHY_COMMENTARIES = gql`
  query frameworkHierarchyCommentaries($frameworkIdentifier: String!, $workspaceId: UxId) {
    frameworkHierarchyCommentaries(frameworkIdentifier: $frameworkIdentifier, workspaceId: $workspaceId) {
      id
      path
      tierLabel
      commentary
      updatedAt
    }
  }
`;

/**
 * CRPD-7267 — a framework's described hierarchy tiers: the names and descriptions behind the
 * grouping keys that `FrameworkControl.hierarchyPath` carries.
 *
 * Keyed on the framework's stable `framework_identifier`, not a ux_id, so the tiers survive the
 * content editor's version bumps. An empty list means nobody has described this framework's tiers
 * yet — a first-run state, not an error; every surface then renders the raw `path`, never the
 * path's last segment.
 *
 * This is also the read behind the "Framework Hierarchy Description" CSV download, so a
 * download/edit/re-upload round trip cannot lose a tier name.
 */
export const FRAMEWORK_HIERARCHY = gql`
  query frameworkHierarchy($frameworkIdentifier: String!) {
    frameworkHierarchy(frameworkIdentifier: $frameworkIdentifier) {
      id
      frameworkIdentifier
      path
      parentPath
      depth
      label
      description
    }
  }
`;
